export const FORMATTED_POWER_VALUE_FALLBACK = '—';

export type PowerBaseUnit = 'w' | 'kw' | 'mw';

export interface IFormattedPowerValue {
  value: string;
  unit: string;
}

interface IPowerUnitScale {
  threshold: number;
  divisor: number;
  unit: string;
}

const POWER_SCALES: IPowerUnitScale[] = [
  { threshold: 1e9, divisor: 1e9, unit: 'monitoring.chart.units.gigawatt' },
  { threshold: 1e6, divisor: 1e6, unit: 'monitoring.chart.units.megawatt' },
  { threshold: 1e3, divisor: 1e3, unit: 'monitoring.chart.units.kilowatt' },
  { threshold: 1, divisor: 1, unit: 'monitoring.chart.units.watt' },
];

const normalizeFiniteNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return value;
};

const getMinimumUnit = (baseUnit: PowerBaseUnit): string => {
  switch (baseUnit) {
    case 'mw':
      return 'monitoring.chart.units.megawatt';
    case 'kw':
      return 'monitoring.chart.units.kilowatt';
    default:
      return 'monitoring.chart.units.watt';
  }
};

const convertToWatts = (value: number, baseUnit: PowerBaseUnit): number => {
  switch (baseUnit) {
    case 'mw':
      return value * 1_000_000;
    case 'kw':
      return value * 1_000;
    default:
      return value;
  }
};

const formatNumericPart = (
  value: number,
  locale: string,
  maximumFractionDigits: number,
  minimumFractionDigits: number,
): string => {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(value);
};

const getFallbackScale = (): IPowerUnitScale => {
  const fallbackScale = POWER_SCALES[POWER_SCALES.length - 1];
  if (fallbackScale === undefined) {
    throw new Error('POWER_SCALES must contain at least one scale.');
  }

  return fallbackScale;
};

export const formatPowerValue = (
  value: unknown,
  {
    baseUnit,
    locale,
    fallback = FORMATTED_POWER_VALUE_FALLBACK,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  }: {
    baseUnit: PowerBaseUnit;
    locale: string;
    fallback?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): IFormattedPowerValue => {
  const minimumUnit = getMinimumUnit(baseUnit);
  const numeric = normalizeFiniteNumber(value);
  if (numeric === null) {
    return {
      value: fallback,
      unit: minimumUnit,
    };
  }

  const baseValue = convertToWatts(numeric, baseUnit);
  const fallbackScale = getFallbackScale();
  const matchingScale =
    POWER_SCALES.find((scale) => Math.abs(baseValue) >= scale.threshold) ?? fallbackScale;

  const minimumScaleIndex = POWER_SCALES.findIndex((scale) => scale.unit === minimumUnit);
  const matchingScaleIndex = POWER_SCALES.findIndex((scale) => scale.unit === matchingScale.unit);

  const unitScale =
    minimumScaleIndex >= 0 && matchingScaleIndex > minimumScaleIndex
      ? POWER_SCALES[minimumScaleIndex]
      : matchingScale;

  return {
    value: formatNumericPart(
      baseValue / unitScale.divisor,
      locale,
      maximumFractionDigits,
      minimumFractionDigits,
    ),
    unit: unitScale.unit,
  };
};
